import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";

const SORTS = [
  { value: "best_selling", label: "Best Selling" },
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
];

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const category = searchParams.get("category") || "";
  const featured = searchParams.get("featured") === "true";
  const q = searchParams.get("q") || "";
  const sort = searchParams.get("sort") || "best_selling";

  useEffect(() => {
    api.categories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(false);
    const params = { sort, limit: 48 };
    if (category) params.category = category;
    if (featured) params.featured = true;
    if (q) params.q = q;
    api
      .products(params)
      .then(setProducts)
      .catch(() => {
        setProducts([]);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [category, featured, q, sort]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const activeCat = categories.find((c) => c.slug === category);
  const title = featured ? "Featured Collection" : q ? `Results for "${q}"` : activeCat ? activeCat.name : "Shop All";

  return (
    <main data-testid="shop-page" className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
      {activeCat ? (
        <Reveal>
          <div className="img-frame relative overflow-hidden rounded-2xl" data-testid="category-banner">
            {activeCat.image && (
              <img src={activeCat.image} alt={activeCat.name} className="h-[300px] w-full object-cover sm:h-[380px]" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/10" />
            <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-white/60">Collection</p>
              <h1 className="font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">{activeCat.name}</h1>
              <p className="mt-2 font-serif text-base italic text-white/85 sm:text-lg">{activeCat.tagline}</p>
              {activeCat.intro && <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/70">{activeCat.intro}</p>}
              <p className="mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-white/60">
                {loading ? "Loading..." : error ? "Catalog syncing" : `${products.length} product${products.length === 1 ? "" : "s"}`}
              </p>
            </div>
          </div>
        </Reveal>
      ) : (
        <Reveal>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-neutral-400">The Catalogue</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl lg:text-5xl">{title}</h1>
          <p className="mt-3 text-sm text-neutral-500 sm:text-base">
            {loading ? "Loading..." : error ? "Catalog is syncing — please refresh in a moment." : `${products.length} product${products.length === 1 ? "" : "s"}`}
          </p>
        </Reveal>
      )}

      {categories.length > 0 && (
        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="category-filters">
          <button
            data-testid="filter-all"
            onClick={() => setParam("category", "")}
            className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all duration-200 ${
              !category && !featured ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 text-neutral-600 hover:border-neutral-900"
            }`}
          >
            All Products
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              data-testid={`filter-${c.slug}`}
              onClick={() => setParam("category", c.slug)}
              className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all duration-200 ${
                category === c.slug ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 text-neutral-600 hover:border-neutral-900"
              }`}
            >
              {c.name}
            </button>
          ))}
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setParam("sort", e.target.value)}
            className="ml-auto rounded-full border border-neutral-200 bg-white px-4 py-2 text-xs font-semibold text-neutral-700 outline-none transition-colors hover:border-neutral-900"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="mt-10 grid grid-cols-2 gap-6 lg:grid-cols-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="aspect-[4/5] animate-pulse rounded-xl bg-neutral-200/70" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="py-24 text-center" data-testid="shop-empty">
          <p className="font-heading text-xl font-semibold text-neutral-900">
            {error ? "Catalog is syncing" : category ? "Coming Soon" : "Nothing found"}
          </p>
          <p className="mt-2 text-sm text-neutral-500">
            {error
              ? "We're connecting to the live store catalog. Please refresh in a moment."
              : category
                ? "No products available yet — we're curating this collection. Check back soon."
                : "Try a different category or search term."}
          </p>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-2 gap-x-5 gap-y-10 lg:grid-cols-4" data-testid="product-grid">
          {products.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 4) * 0.05} y={20}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
      )}
    </main>
  );
}
